import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, BellOff, CircleCheck, Flag, Heart, Pin, PinOff, ThumbsUp } from "lucide-react";
import { TspBadge, TspConfirmDialog, TspFormField } from "../../../components/primitives";
import { formatDate } from "../../../utils/tspFormat";
import { REPLY_MAX, validateReply } from "../forumModel";

const paragraphsOf = (text) =>
  text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

// One topic with all its replies. Reading needs forum.view; replying, liking,
// subscribing and marking helpful need `canParticipate` (forum.create) — a
// flat permission, the same for every topic. Pinning, reporting and marking a
// best answer depend on THIS topic (moderator, or its asker), so those come
// straight from the server's `capabilities` instead of a second, local
// re-derivation of who may do what (see forumModel.js#adaptTopic/adaptReply).
export default function TopicDetail({ topic, category, canParticipate, actions, onReported }) {
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [reportOpen, setReportOpen] = useState(false);

  const submitReply = (event) => {
    event.preventDefault();
    const found = validateReply(text);
    setError(found);
    if (found) {
      document.getElementById("tsp-forum-reply")?.focus();
      return;
    }
    actions.addReply(topic.id, { body: text });
    setText("");
  };

  const confirmReport = () => {
    actions.reportTopic(topic.id);
    setReportOpen(false);
    onReported(topic);
  };

  return (
    <div className="tsp-topic-detail">
      <Link to="/tsp/forum" className="tsp-back-link">
        <ArrowLeft size={15} aria-hidden="true" /> Back to all topics
      </Link>

      <article className="tsp-card tsp-topic-card">
        <div className="tsp-topic-badges">
          {topic.pinned && <TspBadge tone="amber">Pinned</TspBadge>}
          <TspBadge tone={topic.type === "question" ? "blue" : "neutral"}>{topic.type === "question" ? "Question" : "Discussion"}</TspBadge>
          {topic.solved && <TspBadge tone="green">Solved</TspBadge>}
          {category && <TspBadge tone={category.tone}>{category.label}</TspBadge>}
          {topic.reported && <TspBadge tone="primary">Reported</TspBadge>}
        </div>

        <h2 className="tsp-topic-detail-title">{topic.title}</h2>
        <span className="tsp-topic-byline">
          <strong>{topic.author}</strong> · {formatDate(topic.createdAt)} · {topic.views} views
        </span>

        <div className="tsp-topic-body">
          {paragraphsOf(topic.body).map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="tsp-topic-actions">
          <button
            type="button"
            className={`tsp-like-btn ${topic.likedByMe ? "liked" : ""}`}
            aria-pressed={topic.likedByMe}
            disabled={!canParticipate}
            title={canParticipate ? undefined : "Liking needs contributor access"}
            onClick={() => actions.toggleTopicLike(topic.id)}
          >
            <Heart size={15} aria-hidden="true" fill={topic.likedByMe ? "currentColor" : "none"} /> {topic.likedByMe ? "Liked" : "Like"} · {topic.likes}
          </button>

          <button
            type="button"
            className={`tsp-like-btn ${topic.subscribed ? "liked" : ""}`}
            aria-pressed={topic.subscribed}
            disabled={!canParticipate}
            title={canParticipate ? undefined : "Subscribing needs contributor access"}
            onClick={() => actions.toggleSubscribe(topic.id, topic.subscribed)}
          >
            {topic.subscribed ? <BellOff size={15} aria-hidden="true" /> : <Bell size={15} aria-hidden="true" />} {topic.subscribed ? "Unsubscribe" : "Subscribe"}
          </button>

          {topic.capabilities.pin && (
            <button type="button" className="tsp-like-btn" onClick={() => actions.togglePin(topic.id)}>
              {topic.pinned ? <PinOff size={15} aria-hidden="true" /> : <Pin size={15} aria-hidden="true" />} {topic.pinned ? "Unpin" : "Pin"}
            </button>
          )}

          {topic.capabilities.report && (
            <button type="button" className="tsp-like-btn tsp-report-btn" disabled={topic.reported} onClick={() => setReportOpen(true)}>
              <Flag size={15} aria-hidden="true" /> {topic.reported ? "Reported" : "Report"}
            </button>
          )}
        </div>
      </article>

      <section className="tsp-replies" aria-label="Replies">
        <h3>
          {topic.replies.length} {topic.replies.length === 1 ? "Reply" : "Replies"}
        </h3>

        {topic.replies.length === 0 ? (
          <p className="tsp-muted-text">{topic.type === "question" ? "No answers yet. Be the first to help." : "No replies yet."}</p>
        ) : (
          <ul className="tsp-reply-list">
            {topic.replies.map((entry) => (
              <li key={entry.id} className={`tsp-reply ${entry.best ? "tsp-reply-best" : ""}`}>
                <div className="tsp-reply-head">
                  <span>
                    <strong>{entry.author}</strong> <span className="tsp-muted-text">· {formatDate(entry.createdAt)}</span>
                  </span>
                  {entry.best && (
                    <TspBadge tone="green">
                      <CircleCheck size={11} aria-hidden="true" /> Best answer
                    </TspBadge>
                  )}
                </div>
                <div className="tsp-reply-body">
                  {paragraphsOf(entry.body).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                <div className="tsp-reply-actions">
                  <button
                    type="button"
                    className={`tsp-like-btn tsp-like-btn-sm ${entry.helpfulByMe ? "liked" : ""}`}
                    aria-pressed={entry.helpfulByMe}
                    disabled={!canParticipate}
                    title={canParticipate ? undefined : "Marking helpful needs contributor access"}
                    onClick={() => actions.toggleHelpful(topic.id, entry.id)}
                  >
                    <ThumbsUp size={13} aria-hidden="true" /> Helpful · {entry.helpful}
                  </button>
                  {entry.capabilities.mark_best_answer && (
                    <button type="button" className="tsp-like-btn tsp-like-btn-sm" onClick={() => actions.markBest(topic.id, entry.id)}>
                      <CircleCheck size={13} aria-hidden="true" /> {entry.best ? "Unmark best answer" : "Mark as best answer"}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {canParticipate ? (
          <form onSubmit={submitReply} noValidate className="tsp-reply-form">
            <TspFormField id="tsp-forum-reply" label={topic.type === "question" ? "Your answer" : "Your reply"} error={error} hint={`${text.length}/${REPLY_MAX}`}>
              <textarea
                id="tsp-forum-reply"
                className="tsp-field-control"
                rows={4}
                value={text}
                aria-invalid={error ? "true" : undefined}
                aria-describedby={error ? "tsp-forum-reply-error" : "tsp-forum-reply-hint"}
                onChange={(event) => {
                  setText(event.target.value);
                  setError("");
                }}
              />
            </TspFormField>
            <button type="submit" className="tsp-btn tsp-btn-primary">
              Post {topic.type === "question" ? "Answer" : "Reply"}
            </button>
          </form>
        ) : (
          <p className="tsp-muted-text">Replying needs contributor access.</p>
        )}
      </section>

      <TspConfirmDialog
        open={reportOpen}
        title="Report this topic?"
        message="Moderators will be asked to review it. This cannot be undone from here."
        confirmLabel="Report"
        onConfirm={confirmReport}
        onCancel={() => setReportOpen(false)}
      />
    </div>
  );
}
